import { useState } from "react";
import PropTypes from "prop-types";
import CopilotMessageList from "./CopilotMessageList";
import CopilotComposer from "./CopilotComposer";
import { useCopilotShell } from "./CopilotShellContext";
import { getAuthProps } from "../../utils/authentication/authLibrary";
import { COPILOT_WELCOME } from "./copilotUtils";
import { useCopilotChatSession } from "./useCopilotChatSession";
import { useCopilotAttachments } from "./useCopilotAttachments";
import { useCopilotActions } from "./useCopilotActions";
import "./Copilot.scss";

const CopilotChat = ({ compact = false, jobOpeningIdParam: jobOpeningIdProp }) => {
  const [input, setInput] = useState("");
  const { threads: threadApi } = useCopilotShell();
  const { handleAttachFiles, isUploading } = useCopilotAttachments({
    ensureActiveThread: threadApi.ensureActiveThread,
    setThreadAttachments: threadApi.setThreadAttachments,
  });
  const {
    thread,
    messages,
    workflow,
    isLoading,
    isSendingEmailDraft,
    jobOpeningIdParam,
    pageContext,
    send,
    runSuggestion,
    onSendEmailDraft,
  } = useCopilotChatSession({
    jobOpeningIdProp,
    isUploading,
    setInput,
  });
  const { onNavigate, onAction } = useCopilotActions({ runSuggestion });

  const authData = getAuthProps();
  const userFirstName = authData?.user?.firstName || "User";
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  const greeting = `${timeGreeting}, ${userFirstName}`;

  const isWelcomeScreen = !compact && messages.length === 1 && messages[0].content === COPILOT_WELCOME;
  const attachments = thread?.attachments || [];

  return (
    <div className={`copilot-chat-root${compact ? " copilot-chat-root--compact" : ""}`}>
      {isWelcomeScreen ? (
        <div className="copilot-welcome">
          <div className="copilot-welcome__orb-container">
            <div className="copilot-welcome__orb" />
            <div className="copilot-welcome__orb-glow" />
          </div>
          <p className="copilot-welcome__greeting">{greeting}</p>
          <h1 className="copilot-welcome__heading">
            How can I <span className="text-gradient">assist you today?</span>
          </h1>

          <div className="copilot-welcome__cards">
            <div className="copilot-welcome-card" onClick={() => runSuggestion("What needs attention?")}>
              <div className="copilot-welcome-card__icon-wrap card-blue">
                <i className="pi pi-file-edit" />
              </div>
              <h3 className="copilot-welcome-card__title">What needs attention?</h3>
              <p className="copilot-welcome-card__desc">Get an overview of urgent items and pending tasks.</p>
            </div>

            <div className="copilot-welcome-card" onClick={() => runSuggestion("Show candidates waiting for feedback")}>
              <div className="copilot-welcome-card__icon-wrap card-purple">
                <i className="pi pi-users" />
              </div>
              <h3 className="copilot-welcome-card__title">Show candidates waiting for feedback</h3>
              <p className="copilot-welcome-card__desc">List candidates who require evaluation and feedback.</p>
            </div>

            <div className="copilot-welcome-card" onClick={() => runSuggestion("Show follow-ups due today")}>
              <div className="copilot-welcome-card__icon-wrap card-orange">
                <i className="pi pi-calendar" />
              </div>
              <h3 className="copilot-welcome-card__title">Show follow-ups due today</h3>
              <p className="copilot-welcome-card__desc">Review published and active job opening timings.</p>
            </div>
          </div>

          <div className="copilot-welcome__suggestions">
            <span className="copilot-welcome__suggestions-label">
              <i className="pi pi-chevron-down" /> Suggestions
            </span>
            <div className="copilot-welcome__chips">
              {[
                "What needs attention?",
                "Show candidates waiting for feedback",
                "Show follow-ups due today",
                "Show published job openings",
                "Recruitment report for today",
                "Email shortlist"
              ].map((s) => (
                <button
                  key={s}
                  type="button"
                  className="copilot-welcome-chip"
                  onClick={() => runSuggestion(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <CopilotMessageList
          messages={messages}
          isLoading={isLoading || isUploading}
          activeWorkflow={workflow}
          onAction={onAction}
          onNavigate={onNavigate}
          onCancelWorkflow={() => send("cancel")}
          onDraftPrompt={runSuggestion}
          onFormSave={(workflowPatch) => {
            const current = threadApi.ensureActiveThread();
            threadApi.setThreadWorkflow(current.id, workflowPatch);
            send("save interaction", workflowPatch);
          }}
          onFormRefresh={(workflowPatch) => {
            const current = threadApi.ensureActiveThread();
            threadApi.setThreadWorkflow(current.id, workflowPatch);
            send("refresh interaction form", workflowPatch, { quiet: true });
          }}
          onSendEmailDraft={onSendEmailDraft}
          isSendingEmailDraft={isSendingEmailDraft}
        />
      )}

      <CopilotComposer
        input={input}
        setInput={setInput}
        isLoading={isLoading || isUploading}
        module={thread?.module || pageContext?.module}
        jobOpeningId={jobOpeningIdParam || pageContext?.jobOpeningId}
        activeWorkflow={workflow}
        attachments={attachments}
        onSend={send}
        onAttachFiles={handleAttachFiles}
        onDraftPrompt={runSuggestion}
        isWelcomeScreen={isWelcomeScreen}
      />
    </div>
  );
};

CopilotChat.propTypes = {
  compact: PropTypes.bool,
  jobOpeningIdParam: PropTypes.string,
};

export default CopilotChat;
